import React, { useState, useEffect } from 'react';
import { Check, ChevronDown, ChevronUp, Loader, AlertCircle, Video, Clock, Download, Key, X, Search, Users, ExternalLink, Mail, Youtube, Eye, ThumbsUp, Calendar, Heart, Trash2, Plus, RefreshCw, Edit3, Send, Contact2, Home } from 'lucide-react';

// Supabase REST API helper (proxied through backend)
const supabase = {
  async fetch(table, method = 'GET', body = null, filters = '') {
    const res = await fetch('/api/supabase', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ table, method, body, filters }),
    });
    if (!res.ok) throw new Error(`Supabase error: ${res.status}`);
    return await res.json();
  },
  
  async getLists() {
    return await this.fetch('lists', 'GET', null, '?select=*&order=created_at.desc');
  },
  
  async createList(name) {
    const result = await this.fetch('lists', 'POST', { name });
    return result?.[0];
  },
  
  async updateList(listId, name) {
    const result = await this.fetch('lists', 'PATCH', { name }, `?id=eq.${listId}`);
    return result?.[0];
  },
  
  async deleteList(listId) {
    // First delete all creators in the list
    await this.fetch('creators', 'DELETE', null, `?list_id=eq.${listId}`);
    // Then delete the list
    await this.fetch('lists', 'DELETE', null, `?id=eq.${listId}`);
  },
  
  async getCreators(listId = null) {
    const filter = listId ? `?list_id=eq.${listId}` : '?select=*';
    return await this.fetch('creators', 'GET', null, `${filter}&order=created_at.desc`);
  },
  
  async getAllCreatorsWithLists() {
    return await this.fetch('creators', 'GET', null, '?select=*,lists(name)&order=created_at.desc');
  },
  
  async addCreator(listId, channelId, name, data) {
    const result = await this.fetch('creators', 'POST', { list_id: listId, channel_id: channelId, name, data });
    return result?.[0];
  },
  
  async updateCreator(creatorId, data) {
    const result = await this.fetch('creators', 'PATCH', { data }, `?id=eq.${creatorId}`);
    return result?.[0];
  },
  
  async removeCreator(creatorId) {
    await this.fetch('creators', 'DELETE', null, `?id=eq.${creatorId}`);
  },
  
  async isCreatorInList(listId, channelId) {
    const result = await this.fetch('creators', 'GET', null, `?list_id=eq.${listId}&channel_id=eq.${channelId}`);
    return result && result.length > 0;
  },
  
  // Contact history methods
  async getContactHistory(channelId = null) {
    const filter = channelId ? `?channel_id=eq.${channelId}&order=contacted_at.desc` : '?select=*&order=contacted_at.desc';
    return await this.fetch('contact_history', 'GET', null, filter);
  },
  
  async addContactRecord(channelId, channelName, product, method, notes = '') {
    const result = await this.fetch('contact_history', 'POST', { 
      channel_id: channelId, 
      channel_name: channelName,
      product, 
      method, 
      notes 
    });
    return result?.[0];
  },
  
  async deleteContactRecord(recordId) {
    await this.fetch('contact_history', 'DELETE', null, `?id=eq.${recordId}`);
  }
};

const JACO_LOGO_URL = "https://cdn.shopify.com/s/files/1/0819/8117/files/JACO_gold_1963c468-ee9c-427c-9ac5-e0160a09bf20.png?v=1765641172";
const YOUTUBE_API_BASE = 'https://www.googleapis.com/youtube/v3';

const CONTENT_CATEGORIES = [
  { id: 'automotive', name: 'Automotive', topics: ['cars', 'trucks', 'automotive', 'car review', 'auto repair', 'mechanic'] },
  { id: 'tools-diy', name: 'Tools & DIY', topics: ['DIY', 'tools', 'home improvement', 'workshop', 'maker', 'how to'] },
  { id: 'outdoor-adventure', name: 'Outdoor & Adventure', topics: ['camping', 'overlanding', 'off-road', '4x4', 'adventure', 'outdoor gear'] },
  { id: 'tech-gadgets', name: 'Tech & Gadgets', topics: ['tech review', 'gadgets', 'technology', 'unboxing', 'product review'] },
  { id: 'lifestyle', name: 'Lifestyle', topics: ['lifestyle', 'vlog', 'daily vlog', 'life hacks'] },
  { id: 'sports-fitness', name: 'Sports & Fitness', topics: ['fitness', 'sports', 'workout', 'athlete', 'gym'] },
  { id: 'travel', name: 'Travel', topics: ['travel', 'road trip', 'van life', 'RV life', 'travel vlog'] }
];

const SUBSCRIBER_RANGES = [
  { id: '1000-5000', name: '1K - 5K', min: 1000, max: 5000, label: 'Nano' },
  { id: '5000-15000', name: '5K - 15K', min: 5000, max: 15000, label: 'Micro' },
  { id: '15000-50000', name: '15K - 50K', min: 15000, max: 50000, label: 'Micro' },
  { id: '50000-100000', name: '50K - 100K', min: 50000, max: 100000, label: 'Mid-tier' },
  { id: '100000-300000', name: '100K - 300K', min: 100000, max: 300000, label: 'Mid-tier' },
  { id: '300000+', name: '300K+', min: 300000, max: 100000000, label: 'Macro' }
];

// QUALITY THRESHOLDS - creators must meet these minimums
const QUALITY_THRESHOLDS = {
  minAvgViews: 400,           // Minimum 400 average views per video
  minPostsPerMonth: 2,        // Minimum 2 posts per month (1 every 2 weeks)
  minEngagementRate: 1.5,     // Minimum 1.5% engagement rate
  minRecentVideos: 3,         // Must have at least 3 recent videos to analyze
  maxDaysSinceLastPost: 45,   // Last video within 45 days (active channel)
};

const SCRIPT_PROMPTS = [
  { id: 'genuine-problem', name: 'Genuine Problem-Reaction', desc: 'Panic to relief arc', len: '30-45s' },
  { id: 'unexpected-competence', name: 'Unexpected Competence', desc: 'Casually impress', len: '20-30s' },
  { id: 'learned-too-late', name: 'Learned This Too Late', desc: 'Rueful advice', len: '30-45s' },
  { id: 'casual-flex', name: 'Casual Flex', desc: 'Product appears naturally', len: '20-30s' },
  { id: 'teaching', name: 'Teaching Without Permission', desc: 'Most people dont realize', len: '15-25s' },
  { id: 'spontaneous', name: 'Spontaneous Story', desc: 'This is wild energy', len: '25-40s' },
  { id: 'punchy', name: 'Punchy Rhythm', desc: 'Short punchy sentences', len: '15-25s' },
  { id: 'scroll-stopper', name: 'Scroll Stopper', desc: 'Unexpected analogy', len: '20-30s' },
  { id: 'contrarian', name: 'Contrarian Truth', desc: 'What everyone gets wrong', len: '25-40s' }
];

const safeUrl = (url) => {
  try {
    const u = new URL(url);
    return ['https:', 'http:'].includes(u.protocol) ? url : '#';
  } catch {
    return '#';
  }
};

export default function SocialNinja() {
  // PASSWORD PROTECTION
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetch('/api/auth/status')
      .then(r => r.json())
      .then(data => { setIsAuthenticated(!!data.authed); setAuthChecked(true); })
      .catch(() => setAuthChecked(true));
  }, []);

  const handleLogin = async () => {
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput }),
      });
      if (res.ok) {
        setIsAuthenticated(true);
        setPasswordError('');
      } else {
        setPasswordError('Incorrect password');
        setPasswordInput('');
      }
    } catch {
      setPasswordError('Connection error. Please try again.');
    }
  };

  const [activeTab, setActiveTab] = useState('find-influencers');
  const [youtubeKey, setYoutubeKey] = useState(() => { try { return localStorage.getItem('youtube_api_key') || ''; } catch { return ''; } });
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);

  // Scripts state
  const [brandName, setBrandName] = useState('JACO');
  const [productTitle, setProductTitle] = useState('');
  const [mainKeyword, setMainKeyword] = useState('');
  const [features, setFeatures] = useState('');
  const [isGeneratingScripts, setIsGeneratingScripts] = useState(false);
  const [scriptsStep, setScriptsStep] = useState('');
  const [scriptsProgress, setScriptsProgress] = useState(0);
  const [scripts, setScripts] = useState([]);
  const [scriptsError, setScriptsError] = useState('');
  const [expandedScripts, setExpandedScripts] = useState({});

  // Influencers state
  const [influencerProduct, setInfluencerProduct] = useState('');
  const [influencerCategory, setInfluencerCategory] = useState('automotive');
  const [influencerSubRange, setInfluencerSubRange] = useState('15000-50000');
  const [isSearching, setIsSearching] = useState(false);
  const [searchStep, setSearchStep] = useState('');
  const [influencers, setInfluencers] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [expandedInfluencers, setExpandedInfluencers] = useState({});

  // Similar creators state
  const [seedChannels, setSeedChannels] = useState(['', '', '']);
  const [isSearchingSimilar, setIsSearchingSimilar] = useState(false);
  const [similarStep, setSimilarStep] = useState('');
  const [similarCreators, setSimilarCreators] = useState([]);
  const [similarError, setSimilarError] = useState('');
  const [expandedSimilar, setExpandedSimilar] = useState({});

  // Analyze Creator state (new tab)
  const [analyzeChannelUrl, setAnalyzeChannelUrl] = useState('');
  const [analyzeProduct, setAnalyzeProduct] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeStep, setAnalyzeStep] = useState('');
  const [analyzedCreator, setAnalyzedCreator] = useState(null);
  const [analyzeError, setAnalyzeError] = useState('');

  // Edit list name state
  const [editingListId, setEditingListId] = useState(null);
  const [editListName, setEditListName] = useState('');

  // Edit email state
  const [editingEmailCreatorId, setEditingEmailCreatorId] = useState(null);
  const [editedEmail, setEditedEmail] = useState('');

  // Contact history state
  const [contactHistory, setContactHistory] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactCreator, setContactCreator] = useState(null);
  const [contactProduct, setContactProduct] = useState('');
  const [contactMethod, setContactMethod] = useState('manual');
  const [contactNotes, setContactNotes] = useState('');
  const [isRecordingContact, setIsRecordingContact] = useState(false);

  // SAVED LISTS STATE
  // SUPABASE-BACKED SAVED LISTS
  const [dbLists, setDbLists] = useState([]); // Array of {id, name, created_at}
  const [dbCreators, setDbCreators] = useState([]); // Array of {id, list_id, channel_id, name, data, created_at}
  const [isLoadingDb, setIsLoadingDb] = useState(true);
  const [dbError, setDbError] = useState(null);
  
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [creatorToSave, setCreatorToSave] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [selectedListForSave, setSelectedListForSave] = useState('');
  const [showListsTab, setShowListsTab] = useState(false);
  const [selectedListView, setSelectedListView] = useState('all');
  const [expandedSavedCreators, setExpandedSavedCreators] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [copiedItems, setCopiedItems] = useState({});

  // Load lists, creators, and contact history from Supabase on mount
  useEffect(() => {
    loadFromSupabase();
  }, []);
  
  const loadFromSupabase = async () => {
    setIsLoadingDb(true);
    setDbError(null);
    try {
      const [lists, creators, contacts] = await Promise.all([
        supabase.getLists(),
        supabase.fetch('creators', 'GET', null, '?select=*&order=created_at.desc'),
        supabase.getContactHistory()
      ]);
      setDbLists(lists || []);
      setDbCreators(creators || []);
      setContactHistory(contacts || []);
    } catch (e) {
      console.error('Failed to load from Supabase:', e);
      setDbError('Failed to connect to database. Check your connection.');
    } finally {
      setIsLoadingDb(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedItems(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setCopiedItems(prev => ({ ...prev, [key]: false })), 2000);
  };

  const formatSubs = (n) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n?.toString() || '0';
  };

  const extractChannelId = (url) => {
    const patterns = [/youtube\.com\/channel\/([^\/\?]+)/, /youtube\.com\/@([^\/\?]+)/, /youtube\.com\/c\/([^\/\?]+)/];
    for (const p of patterns) {
      const m = url.match(p);
      if (m) return { type: p.toString().includes('@') ? 'handle' : 'id', value: m[1] };
    }
    return null;
  };

  // SAVE TO LIST FUNCTIONS (Supabase-backed)
  const openSaveModal = (creator) => {
    setCreatorToSave(creator);
    setNewListName('');
    setSelectedListForSave(dbLists[0]?.id || '');
    setShowSaveModal(true);
  };

  const saveCreatorToList = async (listId) => {
    if (!creatorToSave || !listId) return;
    setIsSaving(true);
    
    const creatorData = {
      id: creatorToSave.id,
      name: creatorToSave.name,
      thumb: creatorToSave.thumb,
      subs: creatorToSave.subs,
      channelUrl: creatorToSave.channelUrl,
      aboutUrl: creatorToSave.aboutUrl,
      email: creatorToSave.email,
      metrics: creatorToSave.metrics,
      aiAnalysis: creatorToSave.aiAnalysis,
      personalizedEmail: creatorToSave.personalizedEmail,
      whySimilar: creatorToSave.whySimilar,
      score: creatorToSave.score,
      savedFrom: influencerProduct || 'Similar Creators Search',
      recentVids: creatorToSave.recentVids,
      freshOpportunity: creatorToSave.freshOpportunity,
      trendScore: creatorToSave.trendScore
    };

    try {
      // Check if already in list
      const existing = dbCreators.find(c => c.list_id === listId && c.channel_id === creatorToSave.id);
      if (existing) {
        alert('Creator already in this list!');
        setIsSaving(false);
        return;
      }
      
      const newCreator = await supabase.addCreator(listId, creatorToSave.id, creatorToSave.name, creatorData);
      if (newCreator) {
        setDbCreators(prev => [newCreator, ...prev]);
      }
      setShowSaveModal(false);
      setCreatorToSave(null);
    } catch (e) {
      console.error('Failed to save creator:', e);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const createAndSaveToNewList = async () => {
    if (!newListName.trim()) return;
    setIsSaving(true);
    
    try {
      const newList = await supabase.createList(newListName.trim());
      if (newList) {
        setDbLists(prev => [newList, ...prev]);
        await saveCreatorToList(newList.id);
      }
    } catch (e) {
      console.error('Failed to create list:', e);
      alert('Failed to create list. Please try again.');
      setIsSaving(false);
    }
  };

  const removeFromList = async (creatorDbId) => {
    try {
      await supabase.removeCreator(creatorDbId);
      setDbCreators(prev => prev.filter(c => c.id !== creatorDbId));
    } catch (e) {
      console.error('Failed to remove creator:', e);
      alert('Failed to remove. Please try again.');
    }
  };

  const deleteList = async (listId, listName) => {
    if (!window.confirm(`Delete "${listName}" and all creators in it?`)) return;
    
    try {
      await supabase.deleteList(listId);
      setDbLists(prev => prev.filter(l => l.id !== listId));
      setDbCreators(prev => prev.filter(c => c.list_id !== listId));
      if (selectedListView === listId) setSelectedListView('all');
    } catch (e) {
      console.error('Failed to delete list:', e);
      alert('Failed to delete list. Please try again.');
    }
  };

  const getAllSavedCreators = () => {
    const all = [];
    const seen = new Set();
    dbCreators.forEach(c => {
      const list = dbLists.find(l => l.id === c.list_id);
      if (!seen.has(c.channel_id)) {
        seen.add(c.channel_id);
        all.push({ ...c.data, dbId: c.id, listId: c.list_id, lists: [list?.name || 'Unknown'], createdAt: c.created_at });
      } else {
        const existing = all.find(x => x.id === c.channel_id);
        if (existing && list) existing.lists.push(list.name);
      }
    });
    return all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  const getCreatorsInList = (listId) => {
    return dbCreators
      .filter(c => c.list_id === listId)
      .map(c => ({ ...c.data, dbId: c.id, listId: c.list_id, createdAt: c.created_at }));
  };

  const isCreatorSaved = (channelId) => {
    return dbCreators.some(c => c.channel_id === channelId);
  };

  const getCreatorLists = (channelId) => {
    return dbCreators
      .filter(c => c.channel_id === channelId)
      .map(c => dbLists.find(l => l.id === c.list_id)?.name)
      .filter(Boolean);
  };

  const getTotalSavedCount = () => {
    const uniqueChannels = new Set(dbCreators.map(c => c.channel_id));
    return uniqueChannels.size;
  };

  // EDIT LIST NAME
  const startEditingList = (listId, currentName) => {
    setEditingListId(listId);
    setEditListName(currentName);
  };

  const saveListName = async () => {
    if (!editListName.trim() || !editingListId) return;
    try {
      await supabase.updateList(editingListId, editListName.trim());
      setDbLists(prev => prev.map(l => l.id === editingListId ? { ...l, name: editListName.trim() } : l));
      setEditingListId(null);
      setEditListName('');
    } catch (e) {
      console.error('Failed to update list name:', e);
      alert('Failed to update list name.');
    }
  };

  // EDIT EMAIL TEMPLATE
  const startEditingEmail = (creatorDbId, currentEmail) => {
    setEditingEmailCreatorId(creatorDbId);
    setEditedEmail(currentEmail || '');
  };

  const saveEditedEmail = async (creatorDbId) => {
    try {
      const creator = dbCreators.find(c => c.id === creatorDbId);
      if (!creator) return;
      
      const updatedData = { ...creator.data, personalizedEmail: editedEmail };
      await supabase.updateCreator(creatorDbId, updatedData);
      setDbCreators(prev => prev.map(c => c.id === creatorDbId ? { ...c, data: updatedData } : c));
      setEditingEmailCreatorId(null);
      setEditedEmail('');
    } catch (e) {
      console.error('Failed to save email:', e);
      alert('Failed to save email.');
    }
  };

  // CONTACT TRACKING
  const openContactModal = (creator) => {
    setContactCreator(creator);
    setContactProduct(creator.savedFrom || analyzeProduct || influencerProduct || '');
    setContactMethod('manual');
    setContactNotes('');
    setShowContactModal(true);
  };

  const recordContact = async () => {
    if (!contactCreator || !contactProduct.trim()) return;
    setIsRecordingContact(true);
    
    try {
      const newRecord = await supabase.addContactRecord(
        contactCreator.id || contactCreator.channel_id,
        contactCreator.name,
        contactProduct.trim(),
        contactMethod,
        contactNotes.trim()
      );
      if (newRecord) {
        setContactHistory(prev => [newRecord, ...prev]);
      }
      setShowContactModal(false);
      setContactCreator(null);
    } catch (e) {
      console.error('Failed to record contact:', e);
      alert('Failed to record contact.');
    } finally {
      setIsRecordingContact(false);
    }
  };

  const getCreatorContactHistory = (channelId) => {
    return contactHistory.filter(c => c.channel_id === channelId);
  };

  const getContactCount = (channelId) => {
    return contactHistory.filter(c => c.channel_id === channelId).length;
  };

  // API calls
  const callClaude = async (prompt, maxTokens = 4000) => {
    const res = await fetch('/api/anthropic', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-6',
        max_tokens: maxTokens,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message || data.error);
    return data.content.filter(b => b.type === 'text').map(b => b.text).join('\n');
  };

  // Search YouTube for VIDEOS (not channels) - much better results
  const searchYTVideos = async (query, max = 50) => {
    if (!youtubeKey) { setShowApiKeyModal(true); throw new Error('YouTube API key required'); }
    const url = `${YOUTUBE_API_BASE}/search?part=snippet&type=video&q=${encodeURIComponent(query)}&maxResults=${max}&key=${youtubeKey}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`YouTube API error: ${res.status}`);
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    return data.items || [];
  };

  const searchYTChannels = async (query, max = 50) => {
    if (!youtubeKey) { setShowApiKeyModal(true); throw new Error('YouTube API key required'); }
    const url = `${YOUTUBE_API_BASE}/search?part=snippet&type=channel&q=${encodeURIComponent(query)}&maxResults=${max}&key=${youtubeKey}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`YouTube API error: ${res.status}`);
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    return data.items || [];
  };

  const getChannelDetails = async (ids) => {
    if (!youtubeKey || !ids.length) return [];
    const res = await fetch(`${YOUTUBE_API_BASE}/channels?part=snippet,statistics,brandingSettings,contentDetails&id=${ids.join(',')}&key=${youtubeKey}`);
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    return data.items || [];
  };

  const getChannelByHandle = async (handle) => {
    const res = await fetch(`${YOUTUBE_API_BASE}/channels?part=snippet,statistics,brandingSettings,contentDetails&forHandle=${handle}&key=${youtubeKey}`);
    const data = await res.json();
    return data.items?.[0] || null;
  };

  const getPlaylistVideos = async (playlistId, max = 10) => {
    const res = await fetch(`${YOUTUBE_API_BASE}/playlistItems?part=snippet,contentDetails&playlistId=${playlistId}&maxResults=${max}&key=${youtubeKey}`);
    const data = await res.json();
    return data.items || [];
  };

  const getVideoDetails = async (ids) => {
    if (!ids.length) return [];
    const res = await fetch(`${YOUTUBE_API_BASE}/videos?part=snippet,statistics&id=${ids.join(',')}&key=${youtubeKey}`);
    const data = await res.json();
    return data.items || [];
  };

  // IMPROVED DEEP INFLUENCER SEARCH - v3.4
  // NEW APPROACH: Find channels in the NICHE whose audience would care about the product
  // NOT just channels that have already mentioned the product
  const searchInfluencers = async () => {
    if (!influencerProduct.trim()) { setSearchError('Please enter a product'); return; }
    setIsSearching(true); setSearchError(''); setInfluencers([]);
    
    try {
      const cat = CONTENT_CATEGORIES.find(c => c.id === influencerCategory);
      const range = SUBSCRIBER_RANGES.find(r => r.id === influencerSubRange);

      // Step 1: Generate NICHE-FOCUSED queries (not just product-specific)
      setSearchStep('Generating niche search queries...');
      
      // Broad niche queries - find channels in the category
      const nicheQueries = [
        ...cat.topics.map(t => `${t} review`),
        ...cat.topics.map(t => `${t} tips`),
        ...cat.topics.map(t => `best ${t}`),
        ...cat.topics.map(t => `${t} guide`),
        ...cat.topics.map(t => `${t} haul`),
        ...cat.topics.slice(0, 3).map(t => `${t} 2024`),
        ...cat.topics.slice(0, 3).map(t => `${t} essentials`),
      ];
      
      // AI generates product-ADJACENT queries (related topics)
      let adjacentQueries = [];
      try {
        const qPrompt = `I need YouTube search queries to find creators who would be a good fit to promote: "${influencerProduct}"
Category: ${cat.name}

Generate 12 search queries for RELATED/ADJACENT content (NOT the product itself).

Example for "tire inflator":
- BAD: "tire inflator review" (too specific)
- GOOD: "car emergency kit", "road trip essentials", "garage organization", "car maintenance tips", "overlanding gear haul"

The goal: find creators whose AUDIENCE would care about ${influencerProduct}, even if they've never mentioned it.

Return ONLY a JSON array of 12 search strings. Keep them 2-4 words each.`;
        
        const qRes = await callClaude(qPrompt, 800);
        adjacentQueries = JSON.parse(qRes.match(/\[[\s\S]*\]/)[0]);
        console.log('AI adjacent queries:', adjacentQueries);
      } catch (e) {
        console.log('AI query generation failed, using niche queries only');
      }
      
      // A few product-specific queries (to identify who HAS covered it)
      const productQueries = [
        `${influencerProduct} review`,
        `best ${influencerProduct}`,
        `${influencerProduct} test`,
      ];
      
      const allQueries = [...new Set([...nicheQueries, ...adjacentQueries, ...productQueries])];
      console.log(`Total search queries: ${allQueries.length}`);

      // Step 2: Search for VIDEOS across all queries
      setSearchStep('Searching YouTube for niche creators...');
      const channelData = new Map(); // channelId -> { count, mentionedProduct }
      
      // First, search product-specific queries (mark these channels)
      for (const q of productQueries) {
        try {
          const videos = await searchYTVideos(q, 30);
          videos.forEach(v => {
            const chId = v.snippet?.channelId;
            if (chId) {
              if (!channelData.has(chId)) {
                channelData.set(chId, { count: 0, mentionedProduct: false });
              }
              channelData.get(chId).count++;
              channelData.get(chId).mentionedProduct = true;
            }
          });
          await new Promise(r => setTimeout(r, 60));
        } catch (e) {
          console.error('Search failed:', q, e);
        }
      }
      console.log(`Found ${channelData.size} channels that mentioned "${influencerProduct}"`);

      // Then search niche/adjacent queries (the bigger pool)
      const nicheSearchQueries = [...nicheQueries, ...adjacentQueries];
      for (let i = 0; i < Math.min(nicheSearchQueries.length, 25); i++) {
        const q = nicheSearchQueries[i];
        try {
          if (i % 5 === 0) setSearchStep(`Searching niche content (${i + 1}/${Math.min(nicheSearchQueries.length, 25)})...`);
          const videos = await searchYTVideos(q, 30);
          videos.forEach(v => {
            const chId = v.snippet?.channelId;
            if (chId) {
              if (!channelData.has(chId)) {
                channelData.set(chId, { count: 0, mentionedProduct: false });
              }
              channelData.get(chId).count++;
            }
          });
          await new Promise(r => setTimeout(r, 60));
        } catch (e) {
          console.error('Search failed:', q, e);
        }
      }
      
      console.log(`Total unique channels found: ${channelData.size}`);
      if (channelData.size < 10) {
        throw new Error(`Only found ${channelData.size} channels. Try a different category.`);
      }

      // Step 3: Get channel details (in batches of 50, up to 300 channels)
      setSearchStep(`Getting details for ${Math.min(channelData.size, 300)} channels...`);
      const allChannelIds = Array.from(channelData.keys());
      let allDetails = [];
      
      for (let i = 0; i < Math.min(allChannelIds.length, 300); i += 50) {
        const batch = allChannelIds.slice(i, i + 50);
        const details = await getChannelDetails(batch);
        allDetails = [...allDetails, ...details];
        await new Promise(r => setTimeout(r, 60));
      }
      console.log(`Got details for ${allDetails.length} channels`);

      // Step 4: Filter by subscriber range
      const filtered = allDetails.filter(ch => {
        const subs = parseInt(ch.statistics?.subscriberCount || 0);
        const inRange = subs >= range.min * 0.7 && subs <= range.max * 1.3;
        const notHidden = !ch.statistics?.hiddenSubscriberCount;
        return inRange && notHidden;
      });
      
      console.log(`${filtered.length} channels in ${range.name} subscriber range`);
      
      if (filtered.length === 0) {
        const ranges = { '<5K': 0, '5-50K': 0, '50-200K': 0, '200K-1M': 0, '1M+': 0 };
        allDetails.forEach(ch => {
          const subs = parseInt(ch.statistics?.subscriberCount || 0);
          if (subs < 5000) ranges['<5K']++;
          else if (subs < 50000) ranges['5-50K']++;
          else if (subs < 200000) ranges['50-200K']++;
          else if (subs < 1000000) ranges['200K-1M']++;
          else ranges['1M+']++;
        });
        throw new Error(`No channels in ${range.name} range. Found: ${Object.entries(ranges).map(([k,v]) => `${k}: ${v}`).join(', ')}. Try a different subscriber range.`);
      }

      // Sort by niche relevance (how many times they appeared in searches)
      filtered.sort((a, b) => {
        const aData = channelData.get(a.id) || { count: 0 };
        const bData = channelData.get(b.id) || { count: 0 };
        return bData.count - aData.count;
      });

      // Step 5: Analyze top channels (UP TO 100)
      setSearchStep('Analyzing channel quality...');
      const analyzed = [];
      const channelsToAnalyze = filtered.slice(0, 100);
      
      for (let i = 0; i < channelsToAnalyze.length; i++) {
        const ch = channelsToAnalyze[i];
        
        if (i % 10 === 0) {
          setSearchStep(`Analyzing channels (${i}/${channelsToAnalyze.length})...`);
        }
        
        try {
          const uploadsPL = ch.contentDetails?.relatedPlaylists?.uploads;
          if (!uploadsPL) continue;
          
          const vids = await getPlaylistVideos(uploadsPL, 10);
          if (vids.length < 2) continue;
          
          const vidIds = vids.map(v => v.contentDetails?.videoId).filter(Boolean);
          const vidDetails = vidIds.length ? await getVideoDetails(vidIds) : [];
          
          let totViews = 0, totLikes = 0, totComments = 0;
          const vidData = vidDetails.map(v => {
            const views = parseInt(v.statistics?.viewCount || 0);
            const likes = parseInt(v.statistics?.likeCount || 0);
            const comments = parseInt(v.statistics?.commentCount || 0);
            totViews += views; totLikes += likes; totComments += comments;
            return { title: v.snippet?.title, views, likes, publishedAt: v.snippet?.publishedAt };
          });

          const avgViews = vidData.length ? Math.round(totViews / vidData.length) : 0;
          const engRate = totViews > 0 ? ((totLikes + totComments) / totViews * 100) : 0;
          
          // Skip very low performing channels
          if (avgViews < 100 || engRate < 0.5) continue;
          
          // Posting frequency
          const dates = vidData.map(v => new Date(v.publishedAt)).filter(d => !isNaN(d)).sort((a, b) => b - a);
          let postsPerMonth = 0;
          let daysSinceLastPost = 999;
          
          if (dates.length >= 2) {
            const newest = dates[0];
            const oldest = dates[dates.length - 1];
            const months = Math.max((newest - oldest) / (1000*60*60*24*30), 0.5);
            postsPerMonth = parseFloat((dates.length / months).toFixed(1));
            daysSinceLastPost = Math.round((new Date() - newest) / (1000*60*60*24));
          } else if (dates.length === 1) {
            daysSinceLastPost = Math.round((new Date() - dates[0]) / (1000*60*60*24));
            postsPerMonth = 1;
          }

          // Skip inactive channels
          if (daysSinceLastPost > 120) continue;

          // Trend score
          let trendScore = 'stable';
          if (vidData.length >= 4) {
            const recentAvg = vidData.slice(0, 3).reduce((s, v) => s + v.views, 0) / 3;
            const olderAvg = vidData.slice(3).reduce((s, v) => s + v.views, 0) / Math.max(vidData.length - 3, 1);
            if (recentAvg > olderAvg * 1.2) trendScore = 'growing';
            else if (recentAvg < olderAvg * 0.7) trendScore = 'declining';
          }

          const sponsorKeywords = ['sponsor', 'partner', '#ad', 'gifted', 'collab', 'provided by'];
          const sponsorCount = vidData.filter(v => sponsorKeywords.some(kw => (v.title || '').toLowerCase().includes(kw))).length;

          const desc = ch.snippet?.description || '';
          const emailMatch = desc.match(/[\w.-]+@[\w.-]+\.\w+/);
          const hasBizContact = /business|collab|sponsor|partner|inquiry|contact/i.test(desc);

          // Quality tier
          let qualityTier = 'Standard';
          if (avgViews >= 1500 && engRate >= 3 && postsPerMonth >= 3) qualityTier = 'Premium';
          else if (avgViews >= 800 && engRate >= 2 && postsPerMonth >= 2) qualityTier = 'High';
          else if (avgViews >= 300 && engRate >= 1) qualityTier = 'Good';

          const chData = channelData.get(ch.id) || { mentionedProduct: false };

          analyzed.push({
            id: ch.id, 
            name: ch.snippet?.title, 
            thumb: ch.snippet?.thumbnails?.medium?.url || ch.snippet?.thumbnails?.default?.url,
            subs: parseInt(ch.statistics?.subscriberCount || 0), 
            desc: desc.substring(0, 300),
            email: emailMatch?.[0] || null, 
            hasBizContact, 
            recentVids: vidData,
            daysSinceLastPost,
            trendScore,
            mentionedProduct: chData.mentionedProduct,
            nicheRelevance: chData.count || 0,
            metrics: { 
              avgViews, 
              engRate: parseFloat(engRate.toFixed(2)), 
              postsPerMonth, 
              hasSponsors: sponsorCount > 0,
              qualityTier
            }
          });
          
          await new Promise(r => setTimeout(r, 40));
        } catch (e) {
          // Skip failed channels silently
        }
      }

      console.log(`Successfully analyzed ${analyzed.length} quality channels`);
      
      if (analyzed.length === 0) {
        throw new Error('Could not analyze any channels. Try a different category or subscriber range.');
      }

      // Step 6: AI ranking - evaluate AUDIENCE FIT, not just product mentions
      setSearchStep('AI evaluating audience fit...');
      
      const channelsForAI = analyzed
        .sort((a, b) => {
          const tierScore = { Premium: 4, High: 3, Good: 2, Standard: 1 };
          const aScore = (tierScore[a.metrics.qualityTier] || 1) * 1000 + a.metrics.avgViews + (a.metrics.engRate * 100);
          const bScore = (tierScore[b.metrics.qualityTier] || 1) * 1000 + b.metrics.avgViews + (b.metrics.engRate * 100);
          return bScore - aScore;
        })
        .slice(0, 50);

      const aiPrompt = `You are an expert influencer marketing strategist. Evaluate these YouTube creators for promoting: "${influencerProduct}"
Category: ${cat.name}

KEY INSIGHT: Creators who HAVEN'T covered this exact product before are often BETTER prospects - it's fresh content for them and their audience!

CREATORS DATA:
${JSON.stringify(channelsForAI.map(c => ({
  name: c.name, 
  id: c.id, 
  subs: c.subs,
  alreadyCoveredProduct: c.mentionedProduct,
  avgViews: c.metrics.avgViews, 
  engRate: c.metrics.engRate + '%', 
  postsPerMonth: c.metrics.postsPerMonth,
  qualityTier: c.metrics.qualityTier,
  hasEmail: !!c.email, 
  hasBizContact: c.hasBizContact, 
  hasSponsors: c.metrics.hasSponsors,
  recentVideoTitles: c.recentVids.slice(0, 5).map(v => v.title)
})), null, 2)}

EVALUATE EACH CREATOR ON:
1. AUDIENCE FIT (most important): Based on their video titles, would their audience care about ${influencerProduct}?
2. FRESH OPPORTUNITY: If alreadyCoveredProduct=false, this is a PLUS - they can create original content!
3. QUALITY: Engagement, posting frequency, professionalism
4. PARTNERSHIP SIGNALS: Email, business contact, existing sponsorships

Return a JSON array with 15-20 best creators:
[{
  "id": "...",
  "name": "...",
  "score": 1-10,
  "likelihood": "High/Medium/Low",
  "freshOpportunity": true/false,
  "whyAudienceFit": "Why their viewers would care about this product",
  "outreachAngle": "Specific hook for the email based on their content",
  "concerns": "Any concerns or None"
}]

Be inclusive - if a creator's audience MIGHT care about ${influencerProduct}, include them.`;

      const aiRes = await callClaude(aiPrompt, 5000);
      let aiData;
      try { 
        aiData = JSON.parse(aiRes.match(/\[[\s\S]*\]/)[0]); 
      } catch { 
        console.error('AI response:', aiRes);
        throw new Error('AI analysis failed'); 
      }
      
      console.log(`AI selected ${aiData.length} creators`);

      // Step 7: Generate personalized outreach emails
      setSearchStep('Generating personalized outreach...');
      const final = [];
      
      for (const ai of aiData.slice(0, 20)) {
        const ch = channelsForAI.find(c => c.id === ai.id);
        if (!ch) continue;

        const emailPrompt = `Write a 100-word outreach email for product collaboration.

Creator: ${ai.name}
Why their audience fits: ${ai.whyAudienceFit}
Fresh opportunity: ${ai.freshOpportunity ? "YES - they haven't covered this product yet!" : "No - they've covered similar products"}
Outreach angle: ${ai.outreachAngle}
Product: ${influencerProduct}
Brand: ${brandName || 'JACO'}

REQUIRED FORMAT (follow this structure exactly):
1. FIRST: Greeting + compliment their channel + introduce yourself: "Hey [name]! I am loving your channel! My name's Nolan and I'm with the brand JACO. We are the industry-leaders in tire pressure gauges and sell various other auto tools and accessories."
2. SECOND: One sentence relating to their specific recent content (reference a real video topic/theme)
3. THIRD: Introduce the product and why it fits their audience
4. FOURTH: Offer to send free product, no strings attached
5. Keep it casual-professional, like messaging a colleague

NO generic phrases like "reaching out" or "hope this finds you well". NO formal sign-offs.

Write ONLY the email body.`;

        try {
          const emailBody = await callClaude(emailPrompt, 500);
          final.push({
            ...ch, 
            channelUrl: `https://youtube.com/channel/${ch.id}`, 
            aboutUrl: `https://youtube.com/channel/${ch.id}/about`,
            aiAnalysis: { ...ai, whyFit: ai.whyAudienceFit }, 
            personalizedEmail: emailBody.trim(),
            freshOpportunity: ai.freshOpportunity
          });
        } catch (e) {
          final.push({
            ...ch, 
            channelUrl: `https://youtube.com/channel/${ch.id}`, 
            aboutUrl: `https://youtube.com/channel/${ch.id}/about`,
            aiAnalysis: { ...ai, whyFit: ai.whyAudienceFit }, 
            personalizedEmail: `Hi ${ai.name.split(' ')[0]},\n\nLove your content! We make ${influencerProduct} and think it'd resonate with your audience. Happy to send one over free if you'd like to check it out - no obligations.\n\nLet me know!\n\n- ${brandName || 'JACO'} Team`,
            freshOpportunity: ai.freshOpportunity
          });
        }
        
        await new Promise(r => setTimeout(r, 60));
      }

      console.log(`Final results: ${final.length} creators`);
      
      if (!final.length) throw new Error('Could not generate results. Please try again.');

      // Sort: Higher scores first, fresh opportunities get a small boost
      final.sort((a, b) => {
        const aScore = (a.aiAnalysis?.score || 0) + (a.freshOpportunity ? 0.5 : 0);
        const bScore = (b.aiAnalysis?.score || 0) + (b.freshOpportunity ? 0.5 : 0);
        return bScore - aScore;
      });
      
      setInfluencers(final);
      
      const exp = {}; 
      final.slice(0, 3).forEach((_, i) => { exp[i] = true; }); 
      setExpandedInfluencers(exp);
      
    } catch (e) { 
      console.error('Search error:', e);
      setSearchError(e.message); 
    }
    finally { 
      setIsSearching(false); setSearchStep('');
    }
  };


  // Similar Creators Search
  const searchSimilar = async () => {
    const urls = seedChannels.filter(u => u.trim());
    if (!urls.length) { setSimilarError('Enter at least one channel URL'); return; }
    setIsSearchingSimilar(true); setSimilarError(''); setSimilarCreators([]);
    try {
      setSimilarStep('Analyzing seed channels...');
      const seeds = [];
      for (const url of urls) {
        const ext = extractChannelId(url);
        if (!ext) continue;
        let ch;
        if (ext.type === 'handle') ch = await getChannelByHandle(ext.value);
        else { const d = await getChannelDetails([ext.value]); ch = d[0]; }
        if (ch) seeds.push({ id: ch.id, name: ch.snippet?.title, subs: parseInt(ch.statistics?.subscriberCount || 0) });
        await new Promise(r => setTimeout(r, 200));
      }
      if (!seeds.length) throw new Error('Could not fetch channels');

      setSimilarStep('Searching for similar...');
      const avgSubs = seeds.reduce((s, c) => s + c.subs, 0) / seeds.length;
      const qPrompt = `Find similar YouTube creators to: ${seeds.map(s => s.name).join(', ')}. Generate 10 search queries. Return ONLY JSON array.`;
      const qRes = await callClaude(qPrompt, 800);
      let queries;
      try { queries = JSON.parse(qRes.match(/\[[\s\S]*\]/)[0]); } catch { queries = seeds.map(s => s.name + ' similar'); }

      const allIds = new Set();
      const seedIds = new Set(seeds.map(s => s.id));
      for (const q of queries.slice(0, 8)) {
        try {
          const res = await searchYTChannels(q, 25);
          res.forEach(r => { if (r.snippet?.channelId && !seedIds.has(r.snippet.channelId)) allIds.add(r.snippet.channelId); });
          await new Promise(r => setTimeout(r, 100));
        } catch {}
      }

      setSimilarStep('Getting details...');
      const details = await getChannelDetails(Array.from(allIds).slice(0, 50));
      const filtered = details.filter(ch => {
        const subs = parseInt(ch.statistics?.subscriberCount || 0);
        return subs >= avgSubs * 0.2 && subs <= avgSubs * 5 && !ch.statistics?.hiddenSubscriberCount;
      });

      setSimilarStep('AI analyzing similarity...');
      const aiPrompt = `Find 10-15 most similar creators to: ${seeds.map(s => s.name).join(', ')}
Candidates: ${JSON.stringify(filtered.slice(0, 25).map(c => ({ id: c.id, name: c.snippet?.title, subs: c.statistics?.subscriberCount })))}
Return JSON array with 10-15 results: [{"id":"...","name":"...","score":1-10,"whySimilar":"2 sentences","sharedAudience":"description"}]`;
      
      const aiRes = await callClaude(aiPrompt, 2500);
      let aiData;
      try { aiData = JSON.parse(aiRes.match(/\[[\s\S]*\]/)[0]); } catch { throw new Error('Analysis failed'); }

      setSimilarStep('Generating emails...');
      const final = [];
      for (const ai of aiData.slice(0, 15)) {
        const ch = filtered.find(c => c.id === ai.id);
        if (!ch) continue;
        const desc = ch.snippet?.description || '';
        const emailMatch = desc.match(/[\w.-]+@[\w.-]+\.\w+/);

        const emailPrompt = `Write 100-word outreach email. Creator: ${ai.name}. Why similar: ${ai.whySimilar}. Similar to: ${seeds.map(s => s.name).join(', ')}. Brand: ${brandName || 'JACO'} (automotive accessories - industry-leaders in tire pressure gauges).

REQUIRED FORMAT:
1. FIRST: "Hey [name]! I am loving your channel! My name's Nolan and I'm with the brand JACO. We are the industry-leaders in tire pressure gauges and sell various other auto tools and accessories."
2. SECOND: Mention you found them through similar creators research, relate to their content
3. THIRD: Offer to send free product
4. Casual tone, NO formal sign-offs.

ONLY email body.`;
        const emailBody = await callClaude(emailPrompt, 500);

        final.push({
          id: ai.id, name: ai.name, thumb: ch.snippet?.thumbnails?.medium?.url,
          subs: parseInt(ch.statistics?.subscriberCount || 0),
          channelUrl: `https://youtube.com/channel/${ai.id}`, aboutUrl: `https://youtube.com/channel/${ai.id}/about`,
          email: emailMatch?.[0] || null, score: ai.score, whySimilar: ai.whySimilar, sharedAudience: ai.sharedAudience,
          personalizedEmail: emailBody.trim()
        });
        await new Promise(r => setTimeout(r, 100));
      }

      final.sort((a, b) => b.score - a.score);
      setSimilarCreators(final);
      const exp = {}; final.slice(0, 3).forEach((_, i) => { exp[i] = true; }); setExpandedSimilar(exp);
    } catch (e) { setSimilarError(e.message); }
    finally { setIsSearchingSimilar(false); setSimilarStep(''); }
  };

  // Generate Scripts
  const generateScripts = async () => {
    if (!productTitle.trim() || !mainKeyword.trim()) { setScriptsError('Enter product and keyword'); return; }
    setIsGeneratingScripts(true); setScriptsError(''); setScripts([]); setScriptsProgress(0);
    const gen = [];
    try {
      for (let i = 0; i < SCRIPT_PROMPTS.length; i++) {
        const p = SCRIPT_PROMPTS[i];
        setScriptsStep(`Generating: ${p.name}...`);
        setScriptsProgress(Math.round(((i + 1) / SCRIPT_PROMPTS.length) * 100));
        const prompt = `Write a TikTok script for ${mainKeyword}. Style: ${p.desc}. Product: ${productTitle}. Brand: ${brandName}. Features: ${features || 'quality'}. Target: ${p.len}. ONLY script text, natural speech, NO brand mention, NO hashtags. Return ONLY the script.`;
        const script = await callClaude(prompt, 1000);
        gen.push({ ...p, script: script.trim() });
        setScripts([...gen]);
      }
      setScriptsStep('');
      const exp = {}; SCRIPT_PROMPTS.forEach(p => { exp[p.id] = true; }); setExpandedScripts(exp);
    } catch (e) { setScriptsError(e.message); }
    finally { setIsGeneratingScripts(false); setScriptsProgress(0); }
  };

  // ANALYZE SINGLE CREATOR
  const analyzeCreator = async () => {
    if (!analyzeChannelUrl.trim()) { setAnalyzeError('Please enter a channel URL'); return; }
    if (!analyzeProduct.trim()) { setAnalyzeError('Please enter a product to evaluate fit'); return; }
    
    setIsAnalyzing(true);
    setAnalyzeError('');
    setAnalyzedCreator(null);
    
    try {
      // Extract channel ID from URL
      setAnalyzeStep('Finding channel...');
      const ext = extractChannelId(analyzeChannelUrl);
      if (!ext) throw new Error('Invalid YouTube channel URL. Use format: youtube.com/@handle or youtube.com/channel/ID');
      
      let channel;
      if (ext.type === 'handle') {
        channel = await getChannelByHandle(ext.value);
      } else {
        const details = await getChannelDetails([ext.value]);
        channel = details[0];
      }
      
      if (!channel) throw new Error('Channel not found');
      
      // Get recent videos
      setAnalyzeStep('Analyzing recent videos...');
      const uploadsPL = channel.contentDetails?.relatedPlaylists?.uploads;
      if (!uploadsPL) throw new Error('Could not access channel videos');
      
      const vids = await getPlaylistVideos(uploadsPL, 10);
      if (vids.length < 2) throw new Error('Channel has too few videos to analyze');
      
      const vidIds = vids.map(v => v.contentDetails?.videoId).filter(Boolean);
      const vidDetails = vidIds.length ? await getVideoDetails(vidIds) : [];
      
      let totViews = 0, totLikes = 0, totComments = 0;
      const vidData = vidDetails.map(v => {
        const views = parseInt(v.statistics?.viewCount || 0);
        const likes = parseInt(v.statistics?.likeCount || 0);
        const comments = parseInt(v.statistics?.commentCount || 0);
        totViews += views; totLikes += likes; totComments += comments;
        return { title: v.snippet?.title, views, likes, publishedAt: v.snippet?.publishedAt };
      });

      const avgViews = vidData.length ? Math.round(totViews / vidData.length) : 0;
      const engRate = totViews > 0 ? ((totLikes + totComments) / totViews * 100) : 0;
      
      // Posting frequency
      const dates = vidData.map(v => new Date(v.publishedAt)).filter(d => !isNaN(d)).sort((a, b) => b - a);
      let postsPerMonth = 0;
      let daysSinceLastPost = 999;
      
      if (dates.length >= 2) {
        const newest = dates[0];
        const oldest = dates[dates.length - 1];
        const months = Math.max((newest - oldest) / (1000*60*60*24*30), 0.5);
        postsPerMonth = parseFloat((dates.length / months).toFixed(1));
        daysSinceLastPost = Math.round((new Date() - newest) / (1000*60*60*24));
      }

      // Trend score
      let trendScore = 'stable';
      if (vidData.length >= 4) {
        const recentAvg = vidData.slice(0, 3).reduce((s, v) => s + v.views, 0) / 3;
        const olderAvg = vidData.slice(3).reduce((s, v) => s + v.views, 0) / Math.max(vidData.length - 3, 1);
        if (recentAvg > olderAvg * 1.2) trendScore = 'growing';
        else if (recentAvg < olderAvg * 0.7) trendScore = 'declining';
      }

      const sponsorKeywords = ['sponsor', 'partner', '#ad', 'gifted', 'collab', 'provided by'];
      const sponsorCount = vidData.filter(v => sponsorKeywords.some(kw => (v.title || '').toLowerCase().includes(kw))).length;

      const desc = channel.snippet?.description || '';
      const emailMatch = desc.match(/[\w.-]+@[\w.-]+\.\w+/);
      const hasBizContact = /business|collab|sponsor|partner|inquiry|contact/i.test(desc);

      // Quality tier
      let qualityTier = 'Standard';
      if (avgViews >= 1500 && engRate >= 3 && postsPerMonth >= 3) qualityTier = 'Premium';
      else if (avgViews >= 800 && engRate >= 2 && postsPerMonth >= 2) qualityTier = 'High';
      else if (avgViews >= 300 && engRate >= 1) qualityTier = 'Good';

      // AI Analysis
      setAnalyzeStep('AI analyzing fit...');
      const aiPrompt = `You are an influencer marketing expert. Analyze this YouTube creator for promoting: "${analyzeProduct}"

CREATOR DATA:
- Name: ${channel.snippet?.title}
- Subscribers: ${parseInt(channel.statistics?.subscriberCount || 0).toLocaleString()}
- Average views: ${avgViews.toLocaleString()}
- Engagement rate: ${engRate.toFixed(2)}%
- Posts per month: ${postsPerMonth}
- Has business email: ${!!emailMatch}
- Does sponsorships: ${sponsorCount > 0}
- Recent video titles: ${vidData.slice(0, 5).map(v => v.title).join(', ')}

Evaluate:
1. Would their audience care about ${analyzeProduct}?
2. What's the partnership likelihood?
3. What outreach angle would work best?

Return JSON:
{"score": 1-10, "likelihood": "High/Medium/Low", "whyFit": "2 sentences on audience fit", "outreachAngle": "specific hook for email", "concerns": "any concerns or None"}`;

      const aiRes = await callClaude(aiPrompt, 1000);
      let aiData;
      try {
        aiData = JSON.parse(aiRes.match(/\{[\s\S]*\}/)[0]);
      } catch {
        aiData = { score: 5, likelihood: 'Medium', whyFit: 'Unable to analyze fit', outreachAngle: 'General product review', concerns: 'None' };
      }

      // Generate email
      setAnalyzeStep('Generating outreach email...');
      const emailPrompt = `Write a 100-word outreach email for product collaboration.

Creator: ${channel.snippet?.title}
Why they're a fit: ${aiData.whyFit}
Outreach angle: ${aiData.outreachAngle}
Product: ${analyzeProduct}
Brand: ${brandName || 'JACO'}

REQUIRED FORMAT (follow this structure exactly):
1. FIRST: Greeting + compliment their channel + introduce yourself: "Hey [name]! I am loving your channel! My name's Nolan and I'm with the brand JACO. We are the industry-leaders in tire pressure gauges and sell various other auto tools and accessories."
2. SECOND: One sentence relating to their specific recent content (reference a real video topic/theme)
3. THIRD: Introduce the product and why it fits their audience
4. FOURTH: Offer to send free product, no strings attached
5. Keep it casual-professional, like messaging a colleague

NO generic phrases like "reaching out" or "hope this finds you well". NO formal sign-offs.

Write ONLY the email body.`;

      const emailBody = await callClaude(emailPrompt, 500);

      const creatorData = {
        id: channel.id,
        name: channel.snippet?.title,
        thumb: channel.snippet?.thumbnails?.medium?.url || channel.snippet?.thumbnails?.default?.url,
        subs: parseInt(channel.statistics?.subscriberCount || 0),
        desc: desc.substring(0, 300),
        channelUrl: `https://youtube.com/channel/${channel.id}`,
        aboutUrl: `https://youtube.com/channel/${channel.id}/about`,
        email: emailMatch?.[0] || null,
        hasBizContact,
        recentVids: vidData,
        daysSinceLastPost,
        trendScore,
        metrics: {
          avgViews,
          engRate: parseFloat(engRate.toFixed(2)),
          postsPerMonth,
          hasSponsors: sponsorCount > 0,
          qualityTier
        },
        aiAnalysis: aiData,
        personalizedEmail: emailBody.trim(),
        savedFrom: analyzeProduct
      };

      setAnalyzedCreator(creatorData);
      setAnalyzeStep('');
      
    } catch (e) {
      console.error('Analyze error:', e);
      setAnalyzeError(e.message);
    } finally {
      setIsAnalyzing(false);
      setAnalyzeStep('');
    }
  };

  const exportCSV = (data, filename) => {
    const headers = ['Name', 'Subscribers', 'Avg Views', 'Engagement', 'Score', 'URL', 'Email', 'Why Fit', 'Email Template'];
    const rows = data.map(c => [
      c.name, c.subs, c.metrics?.avgViews || '', c.metrics?.engRate ? c.metrics.engRate + '%' : '',
      c.aiAnalysis?.score || c.score || '', c.channelUrl, c.email || 'Check About',
      `"${(c.aiAnalysis?.whyFit || c.whySimilar || '').replace(/"/g, '""')}"`,
      `"${(c.personalizedEmail || '').replace(/"/g, '""')}"`
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Render saved creator card (reusable)
  const renderCreatorCard = (c, i, type = 'influencer') => {
    const isExpanded = type === 'influencer' ? expandedInfluencers[i] : type === 'similar' ? expandedSimilar[i] : type === 'analyze' ? true : expandedSavedCreators[`${type}-${i}`];
    const toggleExpand = () => {
      if (type === 'influencer') setExpandedInfluencers(p => ({ ...p, [i]: !p[i] }));
      else if (type === 'similar') setExpandedSimilar(p => ({ ...p, [i]: !p[i] }));
      else if (type === 'analyze') {} // Always expanded for analyze
      else setExpandedSavedCreators(p => ({ ...p, [`${type}-${i}`]: !p[`${type}-${i}`] }));
    };
    const saved = isCreatorSaved(c.id);
    const creatorLists = getCreatorLists(c.id);
    const contactCount = getContactCount(c.id);
    const creatorDbId = c.dbId; // For saved creators

    return (
      <div key={c.id} className="card" style={{ background: type === 'influencer' && i < 3 ? 'rgba(16,185,129,0.05)' : 'rgba(255,255,255,0.05)', border: `1px solid ${type === 'influencer' && i < 3 ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '16px', marginBottom: '16px', overflow: 'hidden' }}>
        <div onClick={toggleExpand} style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: type === 'analyze' ? 'default' : 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <img src={c.thumb} alt="" style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '600' }}>{c.name}</h3>
                <span style={{ padding: '4px 10px', background: (c.aiAnalysis?.score || c.score) >= 8 ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)', border: `1px solid ${(c.aiAnalysis?.score || c.score) >= 8 ? 'rgba(16,185,129,0.4)' : 'rgba(245,158,11,0.4)'}`, borderRadius: '6px', color: (c.aiAnalysis?.score || c.score) >= 8 ? '#10b981' : '#f59e0b', fontSize: '11px', fontWeight: '600' }}>{c.aiAnalysis?.score || c.score}/10</span>
                {c.metrics?.qualityTier === 'Premium' && <span style={{ padding: '4px 8px', background: 'rgba(234,179,8,0.2)', borderRadius: '6px', color: '#eab308', fontSize: '10px', fontWeight: '600' }}>⭐ PREMIUM</span>}
                {c.metrics?.qualityTier === 'High' && <span style={{ padding: '4px 8px', background: 'rgba(34,197,94,0.2)', borderRadius: '6px', color: '#22c55e', fontSize: '10px', fontWeight: '600' }}>HIGH QUALITY</span>}
                {c.freshOpportunity && <span style={{ padding: '4px 8px', background: 'rgba(168,85,247,0.2)', borderRadius: '6px', color: '#a855f7', fontSize: '10px', fontWeight: '600' }}>🆕 FRESH OPPORTUNITY</span>}
                {c.trendScore === 'growing' && <span style={{ padding: '4px 8px', background: 'rgba(34,197,94,0.2)', borderRadius: '6px', color: '#22c55e', fontSize: '10px', fontWeight: '600' }}>📈 GROWING</span>}
                {c.aiAnalysis?.likelihood === 'High' && <span style={{ padding: '4px 8px', background: 'rgba(59,130,246,0.2)', borderRadius: '6px', color: '#60a5fa', fontSize: '10px', fontWeight: '600' }}>LIKELY TO PARTNER</span>}
                {contactCount > 0 && <span style={{ padding: '4px 8px', background: 'rgba(251,146,60,0.2)', borderRadius: '6px', color: '#fb923c', fontSize: '10px', fontWeight: '600' }}>📧 CONTACTED ({contactCount}x)</span>}
                {saved && <span style={{ padding: '4px 8px', background: 'rgba(239,68,68,0.2)', borderRadius: '6px', color: '#f87171', fontSize: '10px', fontWeight: '600' }}>♥ SAVED</span>}
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', color: '#64748b', fontSize: '12px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Users size={12} />{formatSubs(c.subs)}</span>
                {c.metrics?.avgViews && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Eye size={12} />{formatSubs(c.metrics.avgViews)} avg</span>}
                {c.metrics?.engRate && <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: c.metrics.engRate >= 5 ? '#10b981' : '#64748b' }}><ThumbsUp size={12} />{c.metrics.engRate}%</span>}
                {c.metrics?.postsPerMonth && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Calendar size={12} />{c.metrics.postsPerMonth}/mo</span>}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={(e) => { e.stopPropagation(); openSaveModal(c); }} style={{ background: saved ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)', border: `1px solid ${saved ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.2)'}`, borderRadius: '8px', padding: '8px', color: saved ? '#f87171' : '#94a3b8', cursor: 'pointer' }} title={saved ? `Saved to: ${creatorLists.join(', ')}` : 'Save to list'}><Heart size={16} fill={saved ? '#f87171' : 'none'} /></button>
            {c.email && <Mail size={18} color="#10b981" />}
            {c.metrics?.hasSponsors && <span style={{ background: 'rgba(139,92,246,0.2)', borderRadius: '6px', padding: '4px 8px', color: '#a78bfa', fontSize: '10px', fontWeight: '600' }}>SPONSORS</span>}
            {type !== 'analyze' && (isExpanded ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />)}
          </div>
        </div>
        {isExpanded && (
          <div style={{ padding: '24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            {(c.aiAnalysis?.whyFit || c.whySimilar) && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: '#10b981', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>{c.whySimilar ? 'WHY SIMILAR' : 'WHY GOOD FIT'}</h4>
                <p style={{ color: '#e2e8f0', fontSize: '14px', lineHeight: '1.6' }}>{c.aiAnalysis?.whyFit || c.whySimilar}</p>
                {c.aiAnalysis?.concerns && c.aiAnalysis.concerns !== 'None' && <p style={{ color: '#f59e0b', fontSize: '13px', marginTop: '8px' }}>⚠️ {c.aiAnalysis.concerns}</p>}
              </div>
            )}
            {c.metrics && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '12px' }}><p style={{ color: '#64748b', fontSize: '11px' }}>Subs</p><p style={{ color: '#fff', fontSize: '16px', fontWeight: '600' }}>{formatSubs(c.subs)}</p></div>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '12px' }}><p style={{ color: '#64748b', fontSize: '11px' }}>Avg Views</p><p style={{ color: '#fff', fontSize: '16px', fontWeight: '600' }}>{formatSubs(c.metrics.avgViews)}</p></div>
                <div style={{ background: c.metrics.engRate >= 5 ? 'rgba(16,185,129,0.15)' : 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '12px' }}><p style={{ color: '#64748b', fontSize: '11px' }}>Engagement</p><p style={{ color: c.metrics.engRate >= 5 ? '#10b981' : '#fff', fontSize: '16px', fontWeight: '600' }}>{c.metrics.engRate}%</p></div>
                <div style={{ background: c.email ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)', borderRadius: '10px', padding: '12px' }}><p style={{ color: '#64748b', fontSize: '11px' }}>Email</p><p style={{ color: c.email ? '#10b981' : '#f59e0b', fontSize: '12px', fontWeight: '600', wordBreak: 'break-all' }}>{c.email || 'Check About'}</p></div>
              </div>
            )}
            {c.personalizedEmail && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ color: '#10b981', fontSize: '13px', fontWeight: '600' }}>OUTREACH EMAIL</h4>
                  {creatorDbId && editingEmailCreatorId !== creatorDbId && (
                    <button onClick={(e) => { e.stopPropagation(); startEditingEmail(creatorDbId, c.personalizedEmail); }} style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#94a3b8', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><Edit3 size={12} /> Edit</button>
                  )}
                </div>
                {editingEmailCreatorId === creatorDbId ? (
                  <div>
                    <textarea 
                      value={editedEmail} 
                      onChange={e => setEditedEmail(e.target.value)} 
                      style={{ width: '100%', minHeight: '200px', padding: '16px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: '12px', color: '#e2e8f0', fontSize: '14px', lineHeight: '1.7', resize: 'vertical' }}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button onClick={() => saveEditedEmail(creatorDbId)} style={{ padding: '8px 16px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Save</button>
                      <button onClick={() => { setEditingEmailCreatorId(null); setEditedEmail(''); }} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#94a3b8', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(16,185,129,0.2)' }}><p style={{ color: '#e2e8f0', fontSize: '14px', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{c.personalizedEmail}</p></div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {c.personalizedEmail && (
                <button onClick={() => copyToClipboard(c.personalizedEmail, `e-${type}-${i}`)} style={{ padding: '10px 20px', background: copiedItems[`e-${type}-${i}`] ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.1)', border: `1px solid ${copiedItems[`e-${type}-${i}`] ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.2)'}`, borderRadius: '10px', color: copiedItems[`e-${type}-${i}`] ? '#10b981' : '#94a3b8', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>{copiedItems[`e-${type}-${i}`] ? <Check size={16} /> : <Mail size={16} />}{copiedItems[`e-${type}-${i}`] ? 'Copied!' : 'Copy Email'}</button>
              )}
              <button onClick={(e) => { e.stopPropagation(); openContactModal(c); }} style={{ padding: '10px 20px', background: 'rgba(251,146,60,0.2)', border: '1px solid rgba(251,146,60,0.4)', borderRadius: '10px', color: '#fb923c', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}><Send size={16} /> Mark Contacted</button>
              <a href={safeUrl(c.channelUrl)} target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', color: '#94a3b8', fontSize: '13px', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}><Youtube size={16} /> Channel</a>
              <a href={safeUrl(c.aboutUrl)} target="_blank" rel="noopener noreferrer" style={{ padding: '10px 20px', background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '10px', color: '#60a5fa', fontSize: '13px', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}><ExternalLink size={16} /> About</a>
              <button onClick={() => openSaveModal(c)} style={{ padding: '10px 20px', background: saved ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)', border: `1px solid ${saved ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.2)'}`, borderRadius: '10px', color: saved ? '#f87171' : '#94a3b8', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}><Heart size={16} fill={saved ? '#f87171' : 'none'} />{saved ? 'Saved' : 'Save'}</button>
            </div>
            {/* Contact History */}
            {contactCount > 0 && (
              <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(251,146,60,0.1)', borderRadius: '8px', border: '1px solid rgba(251,146,60,0.2)' }}>
                <p style={{ color: '#fb923c', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>Contact History ({contactCount})</p>
                {getCreatorContactHistory(c.id).slice(0, 3).map((contact, idx) => (
                  <div key={idx} style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
                    {new Date(contact.contacted_at).toLocaleDateString()} - {contact.product} ({contact.method}){contact.notes && ` - ${contact.notes}`}
                  </div>
                ))}
                {contactCount > 3 && <p style={{ fontSize: '11px', color: '#64748b' }}>... and {contactCount - 3} more</p>}
              </div>
            )}
            {saved && creatorLists.length > 0 && (
              <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px' }}>
                <p style={{ color: '#f87171', fontSize: '12px' }}>Saved to: {creatorLists.join(', ')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const totalSaved = getTotalSavedCount();

  // PASSWORD SCREEN
  if (!authChecked) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={40} color="#10b981" style={{ animation: 'pulse 1.5s infinite' }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Inter', sans-serif; }
        `}</style>
        <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', padding: '48px', maxWidth: '400px', width: '100%', textAlign: 'center' }}>
          <img src={JACO_LOGO_URL} alt="JACO" style={{ height: '40px', marginBottom: '24px' }} />
          <h1 style={{ color: '#fff', fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>Social Ninja</h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '32px' }}>Enter password to continue</p>
          
          <input
            type="password"
            value={passwordInput}
            onChange={e => setPasswordInput(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleLogin()}
            placeholder="Password"
            style={{ width: '100%', padding: '16px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${passwordError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.15)'}`, borderRadius: '12px', color: '#fff', fontSize: '16px', textAlign: 'center', marginBottom: '16px' }}
            autoFocus
          />
          
          {passwordError && (
            <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '16px' }}>{passwordError}</p>
          )}
          
          <button
            onClick={handleLogin}
            style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
          >
            Enter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)', padding: '40px 20px' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .card { transition: all 0.2s ease; }
        .card:hover { border-color: rgba(16,185,129,0.4); }
        textarea:focus, input:focus, select:focus { outline: none; border-color: #10b981 !important; }
      `}</style>

      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        
        {/* SAVE TO LIST MODAL */}
        {showSaveModal && creatorToSave && (
          <>
            <div onClick={() => setShowSaveModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1001 }} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '32px', width: '450px', maxWidth: '90vw', zIndex: 1002 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><Heart size={24} color="#f87171" /><h3 style={{ fontSize: '20px', fontWeight: '700', color: '#fff' }}>Save Creator</h3></div>
                <button onClick={() => setShowSaveModal(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', padding: '8px', color: '#94a3b8', cursor: 'pointer' }}><X size={20} /></button>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                <img src={creatorToSave.thumb} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
                <div>
                  <p style={{ color: '#fff', fontWeight: '600' }}>{creatorToSave.name}</p>
                  <p style={{ color: '#64748b', fontSize: '13px' }}>{formatSubs(creatorToSave.subs)} subscribers</p>
                </div>
              </div>

              {dbLists.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', color: '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>ADD TO EXISTING LIST</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {dbLists.map(list => {
                      const alreadyIn = dbCreators.some(c => c.list_id === list.id && c.channel_id === creatorToSave.id);
                      const creatorCount = dbCreators.filter(c => c.list_id === list.id).length;
                      return (
                        <button key={list.id} onClick={() => !alreadyIn && !isSaving && saveCreatorToList(list.id)} disabled={alreadyIn || isSaving} style={{ padding: '12px 16px', background: alreadyIn ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.05)', border: `1px solid ${alreadyIn ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '10px', color: alreadyIn ? '#10b981' : '#fff', fontSize: '14px', cursor: alreadyIn || isSaving ? 'default' : 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{list.name}</span>
                          <span style={{ color: '#64748b', fontSize: '12px' }}>{alreadyIn ? '✓ Already saved' : `${creatorCount} creators`}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label style={{ display: 'block', color: '#10b981', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>CREATE NEW LIST</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="text" value={newListName} onChange={e => setNewListName(e.target.value)} placeholder="e.g., Tire Inflator Campaign" style={{ flex: 1, padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff', fontSize: '14px' }} onKeyPress={e => e.key === 'Enter' && createAndSaveToNewList()} />
                  <button onClick={createAndSaveToNewList} disabled={!newListName.trim() || isSaving} style={{ padding: '12px 20px', background: newListName.trim() && !isSaving ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: newListName.trim() && !isSaving ? 'pointer' : 'not-allowed' }}>{isSaving ? <Loader size={18} className="animate-spin" /> : <Plus size={18} />}</button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* CONTACT TRACKING MODAL */}
        {showContactModal && contactCreator && (
          <>
            <div onClick={() => setShowContactModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1001 }} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '32px', width: '450px', maxWidth: '90vw', zIndex: 1002 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><Send size={24} color="#fb923c" /><h3 style={{ fontSize: '20px', fontWeight: '700', color: '#fff' }}>Record Contact</h3></div>
                <button onClick={() => setShowContactModal(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', padding: '8px', color: '#94a3b8', cursor: 'pointer' }}><X size={20} /></button>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                <img src={contactCreator.thumb} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
                <div>
                  <p style={{ color: '#fff', fontWeight: '600' }}>{contactCreator.name}</p>
                  <p style={{ color: '#64748b', fontSize: '13px' }}>{formatSubs(contactCreator.subs)} subscribers</p>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>PRODUCT *</label>
                <input type="text" value={contactProduct} onChange={e => setContactProduct(e.target.value)} placeholder="e.g., Tire Inflator, Ratchet Straps" style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff', fontSize: '14px' }} />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>METHOD</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['manual', 'email', 'DM', 'other'].map(method => (
                    <button key={method} onClick={() => setContactMethod(method)} style={{ padding: '10px 16px', background: contactMethod === method ? 'rgba(251,146,60,0.2)' : 'rgba(255,255,255,0.05)', border: `1px solid ${contactMethod === method ? 'rgba(251,146,60,0.4)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '8px', color: contactMethod === method ? '#fb923c' : '#94a3b8', fontSize: '13px', cursor: 'pointer', textTransform: 'capitalize' }}>{method}</button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>NOTES (optional)</label>
                <textarea value={contactNotes} onChange={e => setContactNotes(e.target.value)} placeholder="Any notes about this outreach..." style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff', fontSize: '14px', minHeight: '80px', resize: 'vertical' }} />
              </div>

              <button onClick={recordContact} disabled={!contactProduct.trim() || isRecordingContact} style={{ width: '100%', padding: '14px', background: contactProduct.trim() && !isRecordingContact ? 'linear-gradient(135deg, #fb923c, #ea580c)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: contactProduct.trim() && !isRecordingContact ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {isRecordingContact ? <Loader size={18} className="animate-spin" /> : <Check size={18} />}
                {isRecordingContact ? 'Saving...' : 'Record Contact'}
              </button>
            </div>
          </>
        )}

        {/* API Modal */}
        {showApiKeyModal && (
          <>
            <div onClick={() => setShowApiKeyModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1001 }} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '32px', width: '500px', maxWidth: '90vw', zIndex: 1002 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><Key size={24} color="#10b981" /><h3 style={{ fontSize: '20px', fontWeight: '700', color: '#fff' }}>API Keys</h3></div>
                <button onClick={() => setShowApiKeyModal(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', padding: '8px', color: '#94a3b8', cursor: 'pointer' }}><X size={20} /></button>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#10b981', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>YOUTUBE DATA API KEY</label>
                <input type="password" value={youtubeKey} onChange={e => setYoutubeKey(e.target.value)} placeholder="AIza..." style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', color: '#fff', fontSize: '14px', fontFamily: 'monospace' }} />
              </div>
              <button onClick={() => { localStorage.setItem('youtube_api_key', youtubeKey); setShowApiKeyModal(false); }} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Save Key</button>
            </div>
          </>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 0 }}>
            <button
              onClick={() => {
                setActiveTab('find-influencers');
                setShowListsTab(false);
                setShowApiKeyModal(false);
                setShowContactModal(false);
                setShowSaveModal(false);
                setShowTermsModal(false);
                setShowPrivacyModal(false);
              }}
              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: '12px', padding: '12px 16px', color: '#10b981', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600' }}
              title="Home"
            >
              <Home size={18} /> Home
            </button>
          </div>
          <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '8px' }}>
            <button onClick={() => setShowListsTab(!showListsTab)} style={{ background: showListsTab ? 'rgba(239,68,68,0.2)' : totalSaved > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.1)', border: `1px solid ${showListsTab || totalSaved > 0 ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.2)'}`, borderRadius: '12px', padding: '12px', color: showListsTab || totalSaved > 0 ? '#f87171' : '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} title="Saved Lists"><Heart size={18} fill={totalSaved > 0 ? '#f87171' : 'none'} />{totalSaved > 0 && <span style={{ fontSize: '12px', fontWeight: '600' }}>{totalSaved}</span>}</button>
            <button onClick={() => setShowApiKeyModal(true)} style={{ background: youtubeKey ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)', border: `1px solid ${youtubeKey ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`, borderRadius: '12px', padding: '12px', color: youtubeKey ? '#10b981' : '#ef4444', cursor: 'pointer' }}><Key size={18} /></button>
          </div>
          <img src={JACO_LOGO_URL} alt="JACO" style={{ height: '50px', marginBottom: '24px' }} onError={e => { e.target.style.display = 'none'; }} />
          <h1 style={{ fontSize: '48px', fontWeight: '800', background: 'linear-gradient(135deg, #10b981, #059669)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '12px' }}>Social Ninja</h1>
          <p style={{ color: '#94a3b8', fontSize: '18px' }}>Deep influencer vetting • Video analysis • Partnership discovery</p>
        </div>

        {/* SAVED LISTS VIEW */}
        {showListsTab ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px' }}><Heart size={28} color="#f87171" /> Saved Creators</h2>
              <button onClick={() => setShowListsTab(false)} style={{ padding: '10px 20px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', color: '#94a3b8', fontSize: '13px', cursor: 'pointer' }}>← Back to Search</button>
            </div>

            {isLoadingDb ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,0.05)', borderRadius: '20px' }}>
                <Loader size={48} color="#10b981" style={{ marginBottom: '16px', animation: 'pulse 1.5s infinite' }} />
                <p style={{ color: '#94a3b8', fontSize: '16px' }}>Loading saved creators...</p>
              </div>
            ) : dbError ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(239,68,68,0.1)', borderRadius: '20px', border: '1px solid rgba(239,68,68,0.3)' }}>
                <AlertCircle size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
                <p style={{ color: '#f87171', fontSize: '16px' }}>{dbError}</p>
                <button onClick={loadFromSupabase} style={{ marginTop: '16px', padding: '10px 20px', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '10px', color: '#f87171', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}><RefreshCw size={16} /> Retry</button>
              </div>
            ) : totalSaved === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,0.05)', borderRadius: '20px' }}>
                <Heart size={48} color="#64748b" style={{ marginBottom: '16px' }} />
                <p style={{ color: '#94a3b8', fontSize: '16px' }}>No saved creators yet</p>
                <p style={{ color: '#64748b', fontSize: '14px', marginTop: '8px' }}>Click the heart icon on any creator to save them to a list</p>
              </div>
            ) : (
              <>
                {/* List selector */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button onClick={() => setSelectedListView('all')} style={{ padding: '10px 16px', background: selectedListView === 'all' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)', border: `1px solid ${selectedListView === 'all' ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '10px', color: selectedListView === 'all' ? '#f87171' : '#94a3b8', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>All ({totalSaved})</button>
                  {dbLists.map(list => {
                    const listCreatorCount = dbCreators.filter(c => c.list_id === list.id).length;
                    const isEditing = editingListId === list.id;
                    return (
                      <div key={list.id} style={{ display: 'flex', alignItems: 'center' }}>
                        {isEditing ? (
                          <>
                            <input 
                              type="text" 
                              value={editListName} 
                              onChange={e => setEditListName(e.target.value)} 
                              onKeyPress={e => e.key === 'Enter' && saveListName()}
                              style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: '8px 0 0 8px', color: '#fff', fontSize: '13px', width: '150px' }}
                              autoFocus
                            />
                            <button onClick={saveListName} style={{ padding: '8px 10px', background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.4)', borderLeft: 'none', color: '#10b981', cursor: 'pointer' }}><Check size={14} /></button>
                            <button onClick={() => { setEditingListId(null); setEditListName(''); }} style={{ padding: '8px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderLeft: 'none', borderRadius: '0 8px 8px 0', color: '#ef4444', cursor: 'pointer' }}><X size={14} /></button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => setSelectedListView(list.id)} style={{ padding: '10px 16px', background: selectedListView === list.id ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)', border: `1px solid ${selectedListView === list.id ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '10px 0 0 10px', color: selectedListView === list.id ? '#10b981' : '#94a3b8', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>{list.name} ({listCreatorCount})</button>
                            <button onClick={() => startEditingList(list.id, list.name)} style={{ padding: '10px 10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderLeft: 'none', color: '#64748b', cursor: 'pointer' }} title="Rename list"><Edit3 size={14} /></button>
                            <button onClick={() => deleteList(list.id, list.name)} style={{ padding: '10px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderLeft: 'none', borderRadius: '0 10px 10px 0', color: '#ef4444', cursor: 'pointer' }} title="Delete list"><Trash2 size={14} /></button>
                          </>
                        )}
                      </div>
                    );
                  })}
                  <button onClick={loadFromSupabase} style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: '#64748b', cursor: 'pointer' }} title="Refresh from database"><RefreshCw size={14} /></button>
                </div>

                {/* Export button */}
                <div style={{ marginBottom: '20px' }}>
                  <button onClick={() => {
                    const toExport = selectedListView === 'all' ? getAllSavedCreators() : getCreatorsInList(selectedListView);
                    const listName = selectedListView === 'all' ? 'All' : dbLists.find(l => l.id === selectedListView)?.name || 'List';
                    exportCSV(toExport, `SavedCreators_${listName}_${new Date().toISOString().split('T')[0]}.csv`);
                  }} style={{ padding: '10px 16px', background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: '10px', color: '#10b981', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}><Download size={16} /> Export {selectedListView === 'all' ? 'All' : dbLists.find(l => l.id === selectedListView)?.name}</button>
                </div>

                {/* Creator cards */}
                {(selectedListView === 'all' ? getAllSavedCreators() : getCreatorsInList(selectedListView)).map((c, i) => (
                  <div key={c.dbId || c.id} style={{ position: 'relative' }}>
                    {renderCreatorCard(c, i, `saved-${selectedListView}`)}
                    {selectedListView !== 'all' && (
                      <button onClick={() => removeFromList(c.dbId)} style={{ position: 'absolute', top: '20px', right: '120px', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '8px', padding: '8px', color: '#ef4444', cursor: 'pointer' }} title="Remove from list"><Trash2 size={16} /></button>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: 'rgba(255,255,255,0.05)', padding: '8px', borderRadius: '16px', flexWrap: 'wrap' }}>
              {[{ id: 'find-influencers', label: 'Find Influencers', icon: Search }, { id: 'analyze-creator', label: 'Analyze Creator', icon: Contact2 }, { id: 'similar-creators', label: 'Similar Creators', icon: Users }, { id: 'scripts', label: 'Viral Scripts', icon: Video }].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ flex: 1, padding: '14px', background: activeTab === tab.id ? 'rgba(16,185,129,0.2)' : 'transparent', border: activeTab === tab.id ? '1px solid rgba(16,185,129,0.4)' : '1px solid transparent', borderRadius: '12px', color: activeTab === tab.id ? '#10b981' : '#94a3b8', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minWidth: '120px' }}><tab.icon size={16} />{tab.label}</button>
              ))}
            </div>

            {/* ANALYZE CREATOR TAB */}
            {activeTab === 'analyze-creator' && (
              <div>
                <div style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
                  <p style={{ color: '#a855f7', fontSize: '14px', fontWeight: '600' }}>Analyze Any Creator</p>
                  <p style={{ color: '#94a3b8', fontSize: '13px' }}>Paste a YouTube channel URL to get full analysis: stats, engagement metrics, AI-generated outreach email, and partnership score.</p>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '32px', marginBottom: '24px' }}>
                  <div style={{ display: 'grid', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#a855f7', fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>YOUTUBE CHANNEL URL *</label>
                      <input type="text" value={analyzeChannelUrl} onChange={e => setAnalyzeChannelUrl(e.target.value)} placeholder="e.g., https://youtube.com/@ChannelName or youtube.com/channel/UC..." style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#a855f7', fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>PRODUCT TO EVALUATE FIT *</label>
                      <input type="text" value={analyzeProduct} onChange={e => setAnalyzeProduct(e.target.value)} placeholder="e.g., cordless tire inflator, ratchet straps" style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px' }} />
                    </div>
                  </div>
                  {analyzeError && <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '12px', color: '#ef4444', fontSize: '14px' }}>{analyzeError}</div>}
                  {isAnalyzing && <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: '12px', color: '#a855f7', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}><Loader size={20} style={{ animation: 'pulse 1.5s infinite' }} />{analyzeStep}</div>}
                  <button onClick={analyzeCreator} disabled={isAnalyzing} style={{ width: '100%', marginTop: '24px', padding: '18px', background: isAnalyzing ? 'rgba(168,85,247,0.3)' : 'linear-gradient(135deg, #a855f7, #7c3aed)', border: 'none', borderRadius: '14px', color: '#fff', fontSize: '16px', fontWeight: '700', cursor: isAnalyzing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>{isAnalyzing ? <><Loader size={20} /> Analyzing...</> : <><Contact2 size={20} /> Analyze Creator</>}</button>
                </div>

                {analyzedCreator && (
                  <div>
                    <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: '700', marginBottom: '20px' }}>Creator Analysis</h2>
                    {renderCreatorCard(analyzedCreator, 0, 'analyze')}
                  </div>
                )}
              </div>
            )}

            {/* FIND INFLUENCERS TAB */}
            {activeTab === 'find-influencers' && (
              <div>
                <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
                  <p style={{ color: '#10b981', fontSize: '14px', fontWeight: '600' }}>Niche-Based Search</p>
                  <p style={{ color: '#94a3b8', fontSize: '13px' }}>Finds creators whose AUDIENCE would care about your product - even if they've never covered it. 🆕 Fresh Opportunity = they haven't mentioned this product yet (great for original content!).</p>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '32px', marginBottom: '24px' }}>
                  <div style={{ display: 'grid', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#10b981', fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>PRODUCT TO PROMOTE *</label>
                      <input type="text" value={influencerProduct} onChange={e => setInfluencerProduct(e.target.value)} placeholder="e.g., retractable ratchet straps, cordless tire inflator" style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px' }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', color: '#10b981', fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>CATEGORY</label>
                        <select value={influencerCategory} onChange={e => setInfluencerCategory(e.target.value)} style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px' }}>
                          {CONTENT_CATEGORIES.map(c => <option key={c.id} value={c.id} style={{ background: '#1e293b' }}>{c.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', color: '#10b981', fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>SUBSCRIBER RANGE</label>
                        <select value={influencerSubRange} onChange={e => setInfluencerSubRange(e.target.value)} style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px' }}>
                          {SUBSCRIBER_RANGES.map(r => <option key={r.id} value={r.id} style={{ background: '#1e293b' }}>{r.name} ({r.label})</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  {searchError && <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '12px', color: '#ef4444', fontSize: '14px' }}>{searchError}</div>}
                  {isSearching && <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '12px', color: '#10b981', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}><Loader size={20} style={{ animation: 'pulse 1.5s infinite' }} />{searchStep}</div>}
                  <button onClick={searchInfluencers} disabled={isSearching} style={{ width: '100%', marginTop: '24px', padding: '18px', background: isSearching ? 'rgba(16,185,129,0.3)' : 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '14px', color: '#fff', fontSize: '16px', fontWeight: '700', cursor: isSearching ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>{isSearching ? <><Loader size={20} /> Analyzing...</> : <><Search size={20} /> Find & Analyze Creators</>}</button>
                </div>

                {influencers.length > 0 && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <div>
                        <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: '700' }}>Top {influencers.length} Creator Matches</h2>
                        <p style={{ color: '#64748b', fontSize: '13px' }}>Vetted by video analysis & partnership potential</p>
                      </div>
                      <button onClick={() => exportCSV(influencers, `Influencers_${new Date().toISOString().split('T')[0]}.csv`)} style={{ padding: '10px 16px', background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: '10px', color: '#10b981', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}><Download size={16} /> Export</button>
                    </div>
                    {influencers.map((c, i) => renderCreatorCard(c, i, 'influencer'))}
                  </div>
                )}
              </div>
            )}

            {/* SIMILAR CREATORS TAB */}
            {activeTab === 'similar-creators' && (
              <div>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '32px', marginBottom: '24px' }}>
                  <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '20px' }}>Enter up to 3 YouTube channel URLs to find similar creators.</p>
                  {seedChannels.map((url, idx) => (
                    <div key={idx} style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', color: idx === 0 ? '#10b981' : '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>CHANNEL {idx + 1} {idx === 0 ? '*' : '(optional)'}</label>
                      <input type="text" value={url} onChange={e => { const n = [...seedChannels]; n[idx] = e.target.value; setSeedChannels(n); }} placeholder="https://youtube.com/@channel" style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px' }} />
                    </div>
                  ))}
                  {similarError && <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(239,68,68,0.1)', borderRadius: '12px', color: '#ef4444' }}>{similarError}</div>}
                  {isSearchingSimilar && <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(16,185,129,0.1)', borderRadius: '12px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '12px' }}><Loader size={20} style={{ animation: 'pulse 1.5s infinite' }} />{similarStep}</div>}
                  <button onClick={searchSimilar} disabled={isSearchingSimilar} style={{ width: '100%', marginTop: '24px', padding: '18px', background: isSearchingSimilar ? 'rgba(16,185,129,0.3)' : 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '14px', color: '#fff', fontSize: '16px', fontWeight: '700', cursor: isSearchingSimilar ? 'not-allowed' : 'pointer' }}>{isSearchingSimilar ? 'Searching...' : 'Find Similar Creators'}</button>
                </div>
                {similarCreators.length > 0 && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: '700' }}>Found {similarCreators.length} Similar</h2>
                      <button onClick={() => exportCSV(similarCreators, 'SimilarCreators.csv')} style={{ padding: '10px 16px', background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: '10px', color: '#10b981', fontSize: '13px', cursor: 'pointer' }}><Download size={16} /> Export</button>
                    </div>
                    {similarCreators.map((c, i) => renderCreatorCard(c, i, 'similar'))}
                  </div>
                )}
              </div>
            )}

            {/* SCRIPTS TAB */}
            {activeTab === 'scripts' && (
              <div>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '32px', marginBottom: '24px' }}>
                  <div style={{ display: 'grid', gap: '20px' }}>
                    <div><label style={{ display: 'block', color: '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>BRAND</label><input type="text" value={brandName} onChange={e => setBrandName(e.target.value)} placeholder="JACO" style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px' }} /></div>
                    <div><label style={{ display: 'block', color: '#10b981', fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>PRODUCT *</label><input type="text" value={productTitle} onChange={e => setProductTitle(e.target.value)} placeholder="JACO FlowPro Tire Inflator" style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px' }} /></div>
                    <div><label style={{ display: 'block', color: '#10b981', fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>KEYWORD *</label><input type="text" value={mainKeyword} onChange={e => setMainKeyword(e.target.value)} placeholder="cordless tire inflator" style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px' }} /></div>
                    <div><label style={{ display: 'block', color: '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>FEATURES</label><textarea value={features} onChange={e => setFeatures(e.target.value)} rows={2} placeholder="Auto shut-off, USB-C..." style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', color: '#fff', fontSize: '14px', resize: 'vertical' }} /></div>
                  </div>
                  {scriptsError && <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(239,68,68,0.1)', borderRadius: '12px', color: '#ef4444' }}>{scriptsError}</div>}
                  {isGeneratingScripts && <div style={{ marginTop: '20px' }}><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span style={{ color: '#94a3b8', fontSize: '13px' }}>{scriptsStep}</span><span style={{ color: '#10b981', fontWeight: '600' }}>{scriptsProgress}%</span></div><div style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px' }}><div style={{ height: '100%', width: `${scriptsProgress}%`, background: 'linear-gradient(90deg, #10b981, #059669)', borderRadius: '3px', transition: 'width 0.3s' }} /></div></div>}
                  <button onClick={generateScripts} disabled={isGeneratingScripts} style={{ width: '100%', marginTop: '24px', padding: '18px', background: isGeneratingScripts ? 'rgba(16,185,129,0.3)' : 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '14px', color: '#fff', fontSize: '16px', fontWeight: '700', cursor: isGeneratingScripts ? 'not-allowed' : 'pointer' }}>{isGeneratingScripts ? 'Generating...' : `Generate ${SCRIPT_PROMPTS.length} Scripts`}</button>
                </div>
                {scripts.length > 0 && (
                  <div>
                    <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: '700', marginBottom: '20px' }}>Generated Scripts</h2>
                    {scripts.map((s, i) => (
                      <div key={s.id} className="card" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', marginBottom: '16px' }}>
                        <div onClick={() => setExpandedScripts(p => ({ ...p, [s.id]: !p[s.id] }))} style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(16,185,129,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', fontWeight: '700' }}>{i + 1}</div>
                            <div><h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '600' }}>{s.name}</h3><p style={{ color: '#64748b', fontSize: '13px' }}>{s.desc}</p></div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><span style={{ color: '#64748b', fontSize: '12px' }}><Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />{s.len}</span>{expandedScripts[s.id] ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}</div>
                        </div>
                        {expandedScripts[s.id] && (
                          <div style={{ padding: '24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}><p style={{ color: '#e2e8f0', fontSize: '15px', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>{s.script}</p></div>
                            <button onClick={() => copyToClipboard(s.script, s.id)} style={{ padding: '10px 20px', background: copiedItems[s.id] ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', color: copiedItems[s.id] ? '#10b981' : '#94a3b8', fontSize: '13px', cursor: 'pointer' }}>{copiedItems[s.id] ? 'Copied!' : 'Copy Script'}</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: '60px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <p style={{ color: '#64748b', fontSize: '13px' }}>JACO Superior Products • Social Ninja v3.6</p>
          <p style={{ color: '#475569', fontSize: '11px', marginTop: '4px' }}>Supabase • Analyze Creator • Contact Tracking</p>
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <button onClick={() => setShowTermsModal(true)} style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>Terms of Use</button>
            <button onClick={() => setShowPrivacyModal(true)} style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</button>
            <span style={{ color: '#475569', fontSize: '11px' }}>Contact: support@jacosuperior.com</span>
          </div>
          <p style={{ color: '#475569', fontSize: '10px', marginTop: '8px' }}>By using this application, you agree to be bound by the <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>YouTube Terms of Service</a>.</p>
        </div>

        {showTermsModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowTermsModal(false)}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px', maxWidth: '600px', width: '100%', maxHeight: '80vh', overflowY: 'auto', color: '#e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700' }}>Terms of Use</h2>
                <button onClick={() => setShowTermsModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={20} /></button>
              </div>
              <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#94a3b8' }}>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>Last Updated:</strong> February 4, 2026</p>
                <p style={{ marginBottom: '12px' }}>Welcome to Social Ninja ("Application"), operated by JACO Superior Products ("we", "us", "our"). By accessing or using this Application, you agree to be bound by these Terms of Use.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>1. YouTube Terms of Service.</strong> This Application uses YouTube API Services. By using this Application, you are agreeing to be bound by the <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>YouTube Terms of Service</a>.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>2. Permitted Use.</strong> This Application is an internal business tool for JACO Superior Products. Access is restricted to authorized users only. You may not share your access credentials or use this Application for any purpose other than JACO business operations.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>3. API Data.</strong> This Application retrieves publicly available data via the YouTube API Services. YouTube API data displayed in this Application is refreshed regularly and is not stored for more than 30 days. We do not claim ownership of any YouTube content or data.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>4. Disclaimer.</strong> This Application is provided "as is" without warranties of any kind. We are not responsible for the accuracy or completeness of data retrieved from YouTube API Services.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>5. Contact.</strong> For questions about these Terms, contact us at support@jacosuperior.com.</p>
              </div>
            </div>
          </div>
        )}

        {showPrivacyModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowPrivacyModal(false)}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px', maxWidth: '600px', width: '100%', maxHeight: '80vh', overflowY: 'auto', color: '#e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700' }}>Privacy Policy</h2>
                <button onClick={() => setShowPrivacyModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={20} /></button>
              </div>
              <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#94a3b8' }}>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>Last Updated:</strong> February 4, 2026</p>
                <p style={{ marginBottom: '12px' }}>JACO Superior Products ("we", "us", "our") operates the Social Ninja application ("Application"). This Privacy Policy explains how we collect, use, and protect information when you use our Application.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>1. YouTube API Services.</strong> This Application uses YouTube API Services. By using this Application, you are subject to the <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>YouTube Terms of Service</a> and the <a href="http://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>Google Privacy Policy</a>.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>2. Information We Access and Collect.</strong> Through the YouTube API Services, this Application accesses publicly available YouTube channel data including: channel names, subscriber counts, video titles, view counts, like counts, comment counts, channel descriptions, and channel thumbnails. This data is used solely for identifying and evaluating potential content creator partnerships for JACO Superior Products.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>3. How We Use Information.</strong> YouTube API data is used internally by JACO Superior Products to: identify relevant content creators for product partnerships, evaluate channel relevance and audience fit, and generate personalized outreach communications. We do not sell, rent, or share YouTube API data with any external third parties. Data is used exclusively by authorized JACO team members for internal business operations.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>4. Data Storage and Retention.</strong> YouTube API data displayed in this Application is not stored for more than 30 days in compliance with YouTube API Services policies. Saved creator records in our database contain channel identifiers and user-generated notes (such as outreach emails and contact history) which are retained for business operational purposes.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>5. Cookies and Similar Technologies.</strong> This Application may store data locally on your device using browser local storage and cookies to maintain your session, preferences, and authentication state. No third-party tracking cookies are used.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>6. Revoking Access.</strong> You can revoke this Application's access to your data via the <a href="https://security.google.com/settings/security/permissions" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>Google security settings page</a>.</p>
                <p style={{ marginBottom: '12px' }}><strong style={{ color: '#e2e8f0' }}>7. Contact Information.</strong> For questions or concerns about this Privacy Policy, contact us at: support@jacosuperior.com or JACO Superior Products, Evergreen, CO.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
